import axios from 'axios';
import { Action, Module } from 'vuex-module-decorators';
import { Ticket, TicketStatusEnum } from '@gid/models';
import {
  CrudList,
  CrudListFilters,
} from '@gid/vue-common/store/shared/crud-list.module';

export const moduleName = 'tickets';

/** How a ticket relates to a job — surfaces the ~50k orphan tickets. */
export enum TicketJobLinkageEnum {
  ANY = 'any',
  HAS = 'has',
  NONE = 'none',
  SPECIFIC = 'specific',
}

/**
 * The four statuses an agent can still act on. Used as the default status
 * filter so the console opens on a real inbox rather than the closed backlog.
 */
export const ACTIONABLE_TICKET_STATUSES: TicketStatusEnum[] = [
  TicketStatusEnum.C, // open
  TicketStatusEnum.N, // new
  TicketStatusEnum.A, // answered
  TicketStatusEnum.W, // postponed
];

/**
 * Ticket ids are the table's primary key, copied verbatim from the LiveAgent
 * ticket code, and always have the shape ABC-DEFGH-123. A search term of
 * exactly this shape is therefore a unique lookup rather than a text search.
 */
const TICKET_ID_PATTERN = /^[A-Za-z]{3}-[A-Za-z]{5}-\d{3}$/;

export function isTicketId(term: string): boolean {
  return TICKET_ID_PATTERN.test(term.trim());
}

export interface TicketFilters extends CrudListFilters {
  search: string;
  status: TicketStatusEnum[];
  department: string | null;
  jobLinkage: TicketJobLinkageEnum;
  jobName: string | null;
  /**
   * Widen a free-text search past the status filter. Only ~26k of the ~116k
   * tickets carry an actionable status, so a term that only appears on a closed
   * ticket finds nothing under the default inbox preset. Costs a sequential scan
   * (LIKE '%term%' cannot use an index), so it stays opt-in per search.
   */
  searchAllStatuses: boolean;
}

export interface UpdateLiveAgentPayload {
  id: string;
  status?: TicketStatusEnum;
  agentId?: string | null;
  departmentId?: string | null;
}

@Module({ name: moduleName, namespaced: true })
export default class TicketsModule extends CrudList<Ticket> {
  crudEndpoint = '/data-api/ticket';
  filters: TicketFilters = {
    skipPersist: false,
    search: '',
    status: [...ACTIONABLE_TICKET_STATUSES],
    department: null,
    jobLinkage: TicketJobLinkageEnum.ANY,
    jobName: null,
    searchAllStatuses: false,
  };

  /**
   * Push a status / agent / department change through to LiveAgent via the
   * single `PATCH /ticket/:id/live-agent` endpoint. The endpoint echoes only
   * `{ ticketId, pushed }`, not the row, so we patch the row optimistically
   * from the fields we sent (agentId is never persisted — the entity has no
   * assignee column). The caller reconciles a beat later once the enqueued
   * SINGLE_TICKET_SYNC has landed.
   */
  @Action({ rawError: true })
  async updateLiveAgent(payload: UpdateLiveAgentPayload) {
    const { id, ...changes } = payload;
    const { data } = await axios.patch(
      `/data-api/ticket/${id}/live-agent`,
      changes,
    );
    const rowPatch: Partial<Ticket> & { id: string } = { id };
    if (changes.status !== undefined) rowPatch.status = changes.status;
    if (changes.departmentId !== undefined) {
      rowPatch.departmentId = changes.departmentId as string;
    }
    this.context.commit('patchItem', { data: rowPatch });
    return data;
  }

  /** Re-fetch a single row from GID state to reconcile after a write-through. */
  @Action({ rawError: true })
  async reconcileTicket(id: string) {
    const { data } = await axios.get(`/data-api/ticket/${id}`);
    this.context.commit('patchItem', { data });
    return data;
  }

  /**
   * Trigger a LiveAgent sync (the list header "Sync now" button).
   *
   * With a `ticketId` the backend syncs only that conversation; without one it
   * pulls everything whose LiveAgent activity moved since the last sync. Either
   * way the work is queued server-side, so the resolved response only means
   * "accepted" — the caller has to re-read the rows a beat later.
   */
  @Action({ rawError: true })
  async forceSync(payload?: { ticketId?: string }) {
    const { data } = await axios.post(
      `/data-api/ticket/sync`,
      payload?.ticketId ? { ticketId: payload.ticketId } : {},
    );
    return data;
  }
}
